import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * StoryNav - Full-size Story Map Navigation Component
 * 
 * DYNAMIC VIEW CRITERIA:
 * 1. Horizontal tree layout: parent node left, children stacked vertically right
 * 2. "Align Below" positioning: first child aligned with parent, siblings below
 * 3. 1-tier drill-down: only shows current focus + its immediate children
 * 4. Focus state persistence: remembers selection via sessionStorage
 * 5. Orange outline on focused/parent nodes
 * 6. Arrow indicators (→) on nodes with children
 * 7. 50% scale for unfocused siblings when drilled down
 * 8. Click behavior: click sibling to switch focus, click child to navigate
 * 9. Click parent node to return to start view
 */
const StoryNav = () => {
  const navigate = useNavigate();
  const [hoveredNode, setHoveredNode] = useState(null);
  
  // Initialize focusedBranch from sessionStorage to remember state on back navigation
  const [focusedBranch, setFocusedBranch] = useState(() => {
    const saved = sessionStorage.getItem('storyMapFocusedBranch');
    return saved || null;
  });

  // Save focusedBranch to sessionStorage whenever it changes
  React.useEffect(() => {
    if (focusedBranch) {
      sessionStorage.setItem('storyMapFocusedBranch', focusedBranch);
    } else {
      sessionStorage.removeItem('storyMapFocusedBranch');
    }
  }, [focusedBranch]);

  // Node dimensions
  const NODE_WIDTH = 200;
  const NODE_HEIGHT = 55;
  const CHILD_NODE_WIDTH = 160;
  const CHILD_NODE_HEIGHT = 40;
  const H_GAP = 40;
  const V_GAP = 8;

  // Full tree structure
  const branches = [
    {
      id: 'explore',
      label: 'Checking things out',
      color: '#059669',
      path: '/features',
      children: [
        { id: 'explore-overview', label: 'Overview', color: '#047857', path: '/features' },
        { id: 'explore-tiers', label: 'Paid Tier Options', color: '#10b981', path: '/pricing' }
      ]
    },
    {
      id: 'diy',
      label: 'I have a Twee for my website (DIY)',
      color: '#4f46e5',
      path: '/examples',
      children: [
        { id: 'diy-overview', label: 'Overview', color: '#4338ca', path: '/examples' },
        { id: 'diy-samples', label: 'Sample Stories', color: '#6366f1', path: '/examples' },
        { id: 'diy-compiler', label: 'Easy Story Builder', color: '#6366f1', path: '/compiler' }
      ]
    },
    {
      id: 'pro',
      label: 'Help building better stories (PRO)',
      color: '#9333ea',
      path: '/pro',
      children: [
        { id: 'pro-overview', label: 'Overview', color: '#7e22ce', path: '/pro' },
        { id: 'pro-ve', label: 'Visual Editor', color: '#a855f7', path: '/visual-editor?starter=true' },
        { id: 'pro-vsp', label: 'Visual Story Planner', color: '#a855f7', path: '/visual-story-planner' },
        { id: 'pro-shared', label: 'Shared Story (Beta)', color: '#a855f7', path: '/compiler' }
      ]
    },
    {
      id: 'agency',
      label: 'Clients, Advanced CMS (Agency)',
      color: '#d97706',
      path: '/agency',
      children: [
        { id: 'agency-overview', label: 'Overview', color: '#b45309', path: '/agency' },
        { id: 'agency-sitemap', label: 'Sitemap to PathNav', color: '#f59e0b', path: '/sitemap-to-pathnav' },
        { id: 'agency-journey', label: 'Journey Wizard', color: '#f59e0b', path: '/journey-wizard' },
        { id: 'agency-pro', label: 'All PRO Features', color: '#f59e0b', path: '/pro' }
      ]
    }
  ];

  const startNode = {
    id: 'start',
    label: 'What is your interest?',
    color: '#64748b'
  };

  // Calculate positions based on focus state
  const calculatePositions = () => {
    const nodes = [];
    const edges = [];
    
    const startX = 10;
    
    if (focusedBranch === null) {
      // Initial state: start node + all branches at 100%
      const startY = 10;
      const branchX = startX + NODE_WIDTH + H_GAP;
      let branchY = startY;
      
      nodes.push({
        ...startNode,
        x: startX,
        y: startY,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        scale: 1,
        isStart: true,
        isFocused: true
      });
      
      branches.forEach((branch) => {
        nodes.push({
          ...branch,
          x: branchX,
          y: branchY,
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          scale: 1,
          isBranch: true,
          hasChildren: branch.children && branch.children.length > 0
        });
        
        edges.push({
          from: { x: startX + NODE_WIDTH, y: startY + NODE_HEIGHT / 2 },
          to: { x: branchX, y: branchY + NODE_HEIGHT / 2 }
        });
        
        branchY += NODE_HEIGHT + V_GAP;
      });
    } else {
      // Focused state
      const focusedBranchData = branches.find(b => b.id === focusedBranch);
      const focusedChildren = focusedBranchData?.children || [];
      const unfocusedScale = 0.5;
      const unfocusedHeight = NODE_HEIGHT * unfocusedScale;
      const childrenHeight = focusedChildren.length * CHILD_NODE_HEIGHT + (focusedChildren.length - 1) * V_GAP;
      const focusedBranchHeight = Math.max(NODE_HEIGHT, childrenHeight);
      
      const startY = 10;
      const branchX = startX + NODE_WIDTH + H_GAP;
      
      nodes.push({
        ...startNode,
        x: startX,
        y: startY,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        scale: 1,
        isStart: true,
        isFocused: false
      });
      
      let branchY = startY;
      
      branches.forEach((branch) => {
        const isFocused = branch.id === focusedBranch;
        const scale = isFocused ? 1 : unfocusedScale;
        const height = isFocused ? NODE_HEIGHT : unfocusedHeight;
        const width = isFocused ? NODE_WIDTH : NODE_WIDTH * unfocusedScale;
        const allocatedHeight = isFocused ? focusedBranchHeight : unfocusedHeight;
        const nodeY = branchY + (allocatedHeight - height) / 2;
        
        nodes.push({
          ...branch,
          x: branchX,
          y: nodeY,
          width: width,
          height: height,
          scale: scale,
          isBranch: true,
          isFocused: isFocused,
          hasChildren: !isFocused && branch.children && branch.children.length > 0
        });
        
        edges.push({
          from: { x: startX + NODE_WIDTH, y: startY + NODE_HEIGHT / 2 },
          to: { x: branchX, y: nodeY + height / 2 },
          dimmed: !isFocused
        });
        
        if (isFocused && focusedChildren.length > 0) {
          const childX = branchX + width + H_GAP;
          let childY = branchY;
          
          focusedChildren.forEach((child) => {
            nodes.push({
              ...child,
              x: childX,
              y: childY,
              width: CHILD_NODE_WIDTH,
              height: CHILD_NODE_HEIGHT,
              scale: 1,
              isChild: true
            });
            
            edges.push({
              from: { x: branchX + width, y: nodeY + height / 2 },
              to: { x: childX, y: childY + CHILD_NODE_HEIGHT / 2 }
            });
            
            childY += CHILD_NODE_HEIGHT + V_GAP;
          });
        }
        
        branchY += allocatedHeight + V_GAP;
      });
    }
    
    return { nodes, edges };
  };

  const { nodes, edges } = calculatePositions();

  const handleNodeClick = (node) => {
    if (node.isStart) {
      setFocusedBranch(null);
    } else if (node.isBranch) {
      if (node.isFocused) {
        setFocusedBranch(null);
      } else {
        setFocusedBranch(node.id);
      }
    } else if (node.isChild && node.path) {
      navigate(node.path);
    }
  };

  const maxX = Math.max(...nodes.map(n => n.x + n.width)) + 15;
  const maxY = Math.max(...nodes.map(n => n.y + n.height)) + 15;
  const svgWidth = Math.max(500, maxX);
  const svgHeight = Math.max(280, maxY);

  const renderLabel = (node) => {
    const baseFontSize = node.isChild ? 11 : 12;
    const fontSize = baseFontSize * (node.scale || 1);
    const maxCharsPerLine = node.scale < 1 ? 10 : (node.isChild ? 14 : 20);
    
    const words = node.label.split(' ');
    let lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
        currentLine = (currentLine + ' ' + word).trim();
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    
    if (node.scale && node.scale < 1 && lines.length > 2) {
      lines = lines.slice(0, 2);
      lines[1] = lines[1].substring(0, 8) + '...';
    }
    
    if (lines.length > 1) {
      const lineHeight = fontSize * 1.15;
      const totalTextHeight = lines.length * lineHeight;
      const startY = (node.height - totalTextHeight) / 2 + fontSize * 0.8;
      
      return (
        <text x={node.width / 2} y={startY} textAnchor="middle" fill="white" fontSize={fontSize} fontWeight="600" style={{ pointerEvents: 'none' }}>
          {lines.map((line, i) => (<tspan key={i} x={node.width / 2} dy={i === 0 ? 0 : lineHeight}>{line}</tspan>))}
        </text>
      );
    }
    
    return (
      <text x={node.width / 2} y={node.height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize={fontSize} fontWeight="600" style={{ pointerEvents: 'none' }}>
        {node.label}
      </text>
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
          {edges.map((edge, idx) => (
            <path
              key={idx}
              d={`M ${edge.from.x} ${edge.from.y} C ${edge.from.x + 25} ${edge.from.y}, ${edge.to.x - 25} ${edge.to.y}, ${edge.to.x} ${edge.to.y}`}
              fill="none" stroke="#cbd5e1" strokeWidth="2" opacity={edge.dimmed ? 0.4 : 0.8}
            />
          ))}
          {nodes.map((node) => (
            <g
              key={node.id}
              transform={`translate(${node.x}, ${node.y})`}
              onClick={() => handleNodeClick(node)}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              style={{ cursor: 'pointer' }}
            >
              {(node.isFocused || (node.isStart && focusedBranch === null)) && (
                <rect x="-3" y="-3" width={node.width + 6} height={node.height + 6} rx="10" fill="none" stroke="#f97316" strokeWidth="3" />
              )}
              <rect
                width={node.width} height={node.height} rx={node.scale < 1 ? 4 : 8}
                fill={node.color} opacity={hoveredNode === node.id ? 1 : (node.scale < 1 ? 0.6 : 0.9)}
                className="transition-all duration-200"
                style={{ filter: hoveredNode === node.id ? 'brightness(1.1)' : 'none' }}
              />
              {node.hasChildren && !node.isFocused && node.scale === 1 && (
                <text x={node.width - 15} y={node.height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="14" opacity="0.7">→</text>
              )}
              {renderLabel(node)}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

export default StoryNav;
